import { MEDIAPIPE_SKELETON } from './MediaPipeSkeleton';
import type { MediaPipeOptions } from './MediaPipePoseEngine';
import { PoseWorkerClient } from './PoseWorkerClient';
import {
  sourceHeight,
  sourceWidth,
  type PoseEngine,
  type PoseSource,
  type RawPose,
  type RawPoseResult,
  type SkeletonDefinition,
} from './PoseEngine';

function clampPoses(n: number): number {
  return Math.max(1, Math.min(5, Math.floor(n)));
}

export class MediaPipePoseEngineWorker implements PoseEngine {
  readonly id = 'mediapipe-pose-landmarker-lite-worker';
  readonly label = 'MediaPipe worker (33pt)';
  readonly skeleton: SkeletonDefinition = MEDIAPIPE_SKELETON;
  readonly supportsMultiPose = true;
  private readonly client = new PoseWorkerClient();
  private numPoses: number;

  constructor(private readonly opts: MediaPipeOptions) {
    this.numPoses = clampPoses(opts.numPoses ?? 1);
  }

  async initialize(): Promise<void> {
    this.client.start();
    await this.client.request({
      type: 'init',
      mediapipe: {
        wasmBaseUrl: this.opts.wasmBaseUrl,
        modelAssetPath: this.opts.modelAssetPath,
        numPoses: this.numPoses,
        minDetectionConfidence: this.opts.minDetectionConfidence,
        minTrackingConfidence: this.opts.minTrackingConfidence,
        minPresenceConfidence: this.opts.minPresenceConfidence,
      },
    });
  }

  async setNumPoses(n: number): Promise<void> {
    const next = clampPoses(n);
    if (next === this.numPoses) return;
    this.numPoses = next;
    await this.client.request({ type: 'setNumPoses', n: next });
  }

  async detectForVideo(source: PoseSource, timestamp: number): Promise<RawPoseResult> {
    if (sourceWidth(source) <= 0 || sourceHeight(source) <= 0) return { poses: [] };
    const bitmap = await createImageBitmap(source);
    const result = (await this.client.request({ type: 'detect', bitmap, timestamp }, [bitmap])) as {
      poses: RawPose[];
    };
    return { poses: result.poses ?? [] };
  }

  dispose(): void {
    this.client.terminate();
  }
}
